/**
 * A administração **desta organização** — e a frase em negrito é a tela inteira.
 *
 * Um ADMIN da Rodovia Norte administra a Rodovia Norte. A visão entre clientes — suporte,
 * faturamento, infraestrutura — é a `T-09`, e ela exige papel com `BYPASSRLS`, ADR próprio
 * e trilha de quem olhou o quê. Ela não entra de carona aqui: uma tela chamada "admin" é o
 * lugar mais provável do sistema para uma garantia ser afrouxada "só neste caso", porque
 * quem a usa é o dono do sistema.
 *
 * As seções vêm em **cinco grupos** (a revisão de 06/10: oito pílulas soltas numa linha, que
 * quebravam em três no notebook e não diziam o que cada uma era) — Pessoas e acesso,
 * Integrações, WhatsBI Cockpit, Portal de pagamento e Organização. No computador, uma coluna à esquerda com os
 * grupos; no celular, um seletor só. Cada seção abre com o título e uma frase do que ela
 * resolve. E cada uma responde a uma pergunta que, antes, só se respondia com SQL:
 *
 * - **Quem tem acesso** — e a tela não deixa o último administrador se trancar para fora.
 * - **Chaves de pórtico** — emitir e revogar era SQL na mão (risco `R-07`). O segredo
 *   aparece **uma vez**, na emissão, e não há rota que o mostre de novo.
 * - **Licenças e consumo** — lado a lado de propósito: consumo sem licença é número sem
 *   contexto, licença sem consumo é contrato que ninguém sabe se está sendo usado.
 * - **Tarifas do contrato** (`SC-12`, ADR-084) — a estrutura tarifária que o perfil do poder
 *   concedente aponta e o histórico do cadastro, só leitura (`TarifasDoContrato.tsx`).
 * - **Configuração e panorama** — os parâmetros por concessão (`T-04`) e o "o sistema está
 *   bem?" respondido sem abrir terminal.
 * - **Integração SENATRAN** (ADR-035) — o cadastro que o portal da SENATRAN pede, a URL e
 *   a chave do webhook, e o arquivo da carga histórica. Em `IntegracaoSenatran.tsx`.
 * - **IAs e fontes de dados** (21/09) — quais IAs estão servindo e como foram no dia, e de
 *   onde os trânsitos chegam. Banco e data lake aparecem como "não configurado", com os
 *   campos que a conexão vai pedir: hoje tudo entra por upload ou API de pórtico.
 * - **WhatsBI Cockpit** (ADR-043) — o WhatsApp da concessionária (os dados da Meta, a URL e
 *   o token do webhook) e os celulares de quem pode perguntar (`WhatsBiCockpit.tsx`); o
 *   modelo de linguagem e o consumo (ADR-044); e as **conversas** — quem perguntou o quê,
 *   com o prazo de guarda (`W-15`, `ConversasDoWhatsBi.tsx`).
 * - **Portal de pagamento** (`PG-01`, ADR-081) — o portal de cada concessão: o endereço, o nome,
 *   o domínio, o contato e se está no ar (`PortalDePagamento.tsx`). A página do público é a
 *   outra entrada do build (`portal.html`, `src/portal/`).
 */
import { useCallback, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { ErroDaApi, api } from "../api/cliente";
import { ContextoDaSessao } from "../api/contexto-sessao";
import type {
  ChaveDoPortico,
  ChaveEmitida,
  ConcessaoDaOrganizacao,
  FonteDeDados,
  PainelDeLicencas,
  TetosDoContrato,
  Panorama,
  SituacaoDasIas,
  UsuarioDaOrganizacao,
  ValorDaConfiguracao,
} from "../api/tipos";
import {
  Badge,
  Botao,
  Cartao,
  Estado,
  Linha,
  Painel,
} from "../componentes/base";
import { Icone } from "../componentes/icones";
import { COR_DA_COTA, dataDaCota, usoDaCota } from "../cotas";
import { dataEHora, numeroLongo } from "../formato";
import { ColetaDosPorticos } from "./ColetaDosPorticos";
import { ConversasDoWhatsBi } from "./ConversasDoWhatsBi";
import { DestinosDasNotificacoes } from "./DestinosDasNotificacoes";
import { GuardaDasImagens } from "./GuardaDasImagens";
import { IntegracaoSenatran } from "./IntegracaoSenatran";
import { PortalDePagamento } from "./PortalDePagamento";
import { TarifasDoContrato } from "./TarifasDoContrato";
import { TrilhaAdministrativa } from "./TrilhaAdministrativa";
import { ModeloEConsumo, WhatsAppECelulares } from "./WhatsBiCockpit";
import type { ReactNode } from "react";

type Secao =
  | "usuarios"
  | "chaves"
  | "coleta"
  | "senatran"
  | "ias"
  | "notificacoes"
  | "whatsbi"
  | "modelo"
  | "conversas"
  | "portal"
  | "licencas"
  | "tarifas"
  | "guarda"
  | "sistema"
  | "trilha";

interface DefinicaoDeSecao {
  chave: Secao;
  rotulo: string;
  icone: ReactNode;
  /** Uma frase: o que a seção resolve. */
  descricao: string;
}

const GRUPOS: { rotulo: string; secoes: DefinicaoDeSecao[] }[] = [
  {
    rotulo: "Pessoas e acesso",
    secoes: [
      {
        chave: "usuarios",
        rotulo: "Usuários e perfis",
        icone: <Icone.Pessoas />,
        descricao:
          "Quem entra no cockpit, com que perfil e em quais concessões — e a senha e o segundo fator de cada um.",
      },
      {
        chave: "chaves",
        rotulo: "Chaves de pórtico",
        icone: <Icone.Chave />,
        descricao:
          "As credenciais com que os pórticos mandam as passagens. O segredo aparece uma vez, na emissão.",
      },
    ],
  },
  {
    rotulo: "Integrações",
    secoes: [
      {
        chave: "senatran",
        rotulo: "Integração SENATRAN",
        icone: <Icone.Enviar />,
        descricao:
          "O cadastro que o portal da SENATRAN pede, o webhook do retorno e a carga histórica.",
      },
      {
        chave: "coleta",
        rotulo: "Coleta dos pórticos",
        icone: <Icone.Recarga />,
        descricao:
          "Se cada pórtico entrega as passagens ou o coletor da concessionária as busca — e, na busca, o último sinal do coletor.",
      },
      {
        chave: "notificacoes",
        rotulo: "Notificações aos sistemas",
        icone: <Icone.Alerta />,
        descricao:
          "Para onde a Sugestão de Autuação e a operadora da TAG recebem as notificações, e o segredo da assinatura.",
      },
      {
        chave: "ias",
        rotulo: "IAs e fontes de dados",
        icone: <Icone.Alvo />,
        descricao:
          "Quais IAs estão servindo e como foram nas últimas 24 horas, e de onde os trânsitos chegam.",
      },
    ],
  },
  {
    rotulo: "WhatsBI Cockpit",
    secoes: [
      {
        chave: "whatsbi",
        rotulo: "WhatsApp e celulares",
        icone: <Icone.Conversa />,
        descricao:
          "O número da concessionária na Meta, o webhook e quem pode perguntar pelo WhatsApp.",
      },
      {
        chave: "modelo",
        rotulo: "Modelo e consumo",
        icone: <Icone.Dinheiro />,
        descricao:
          "A chave da OpenAI da organização, o orçamento do mês e o que o agente consumiu.",
      },
      {
        chave: "conversas",
        rotulo: "Conversas",
        icone: <Icone.Lista />,
        descricao:
          "Quem perguntou o quê, na tela e no WhatsApp, e por quanto tempo as conversas ficam guardadas.",
      },
    ],
  },
  {
    rotulo: "Portal de pagamento",
    secoes: [
      {
        chave: "portal",
        rotulo: "Portal de cada concessão",
        icone: <Icone.Dinheiro />,
        descricao:
          "O endereço, o nome, o domínio e o contato do portal onde quem passou consulta as passagens em aberto — e se ele está no ar.",
      },
    ],
  },
  {
    rotulo: "Organização",
    secoes: [
      {
        chave: "licencas",
        rotulo: "Licenças e consumo",
        icone: <Icone.Dinheiro />,
        descricao:
          "Os módulos contratados e as chamadas de IA dos últimos 30 dias — cada uma é uma linha de fatura.",
      },
      {
        chave: "tarifas",
        rotulo: "Tarifas do contrato",
        icone: <Icone.Dinheiro />,
        descricao:
          "As categorias, os multiplicadores e os pórticos do contrato de cada concessão, e o histórico do que está cadastrado.",
      },
      {
        chave: "guarda",
        rotulo: "Guarda das imagens",
        icone: <Icone.Escudo />,
        descricao:
          "Por quanto tempo as fotos de pórtico ficam, pela regra de cada concessão, e o que o comando diário já apagou e travou.",
      },
      {
        chave: "sistema",
        rotulo: "Configuração e saúde",
        icone: <Icone.Engrenagem />,
        descricao:
          "Os parâmetros por concessão e o panorama: volume, imagens, prazos abertos e acesso.",
      },
      {
        chave: "trilha",
        rotulo: "Trilha administrativa",
        icone: <Icone.Escudo />,
        descricao:
          "Toda ação de administração — e toda leitura de conversa —, com quem, quando e de onde.",
      },
    ],
  },
];

const SECOES = GRUPOS.flatMap((g) => g.secoes);

export function Administracao() {
  // A seção vai na URL: a pílula "IAs ativas" da Operação abre direto na seção das IAs.
  const [parametros, definirParametros] = useSearchParams();
  const pedida = parametros.get("secao");
  const secao: Secao = SECOES.some((s) => s.chave === pedida)
    ? (pedida as Secao)
    : "usuarios";
  const definirSecao = (chave: Secao) => definirParametros({ secao: chave });

  const atual = SECOES.find((s) => s.chave === secao)!;

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-titulo-tela leading-none font-extrabold tracking-[-0.01em] text-tinta uppercase">
            Administração
          </h1>
          <p className="text-subtitulo-tela text-tinta-3">
            Pessoas e acesso, integrações, WhatsBI e a organização
          </p>
        </div>
        {/* O escopo dito onde ele é lido, e não num rodapé: quem abre esta tela precisa
            saber, antes de procurar, que ela não mostra os outros clientes. */}
        <span className="ml-auto inline-flex items-center gap-1.5 rounded-pilula border border-linha bg-painel-2 px-2.5 py-1 text-[11px] text-tinta-3">
          <Icone.Escudo />
          Só esta organização — a visão entre clientes é a T-09
        </span>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-[220px_minmax(0,1fr)] md:items-start">
        {/* No celular, um seletor só: oito pílulas viravam três linhas antes do conteúdo. */}
        <label className="grid gap-1 text-[11px] text-tinta-3 md:hidden">
          Seção
          <select
            value={secao}
            onChange={(e) => definirSecao(e.target.value as Secao)}
            className="min-h-10 rounded-botao border border-linha bg-painel-2 px-2 text-[13px] text-tinta"
          >
            {GRUPOS.map((g) => (
              <optgroup key={g.rotulo} label={g.rotulo}>
                {g.secoes.map((s) => (
                  <option key={s.chave} value={s.chave}>
                    {s.rotulo}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>

        <nav
          aria-label="Seções da administração"
          className="hidden gap-3 rounded-cartao border border-linha bg-painel p-2 md:sticky md:top-0 md:grid"
        >
          {GRUPOS.map((g) => (
            <div key={g.rotulo} className="grid gap-0.5">
              <h2 className="px-2 pb-0.5 text-rotulo font-bold tracking-[0.08em] text-tinta-3 uppercase">
                {g.rotulo}
              </h2>
              <div
                role="tablist"
                aria-label={g.rotulo}
                aria-orientation="vertical"
                className="grid gap-0.5"
              >
                {g.secoes.map((s) => (
                  <button
                    key={s.chave}
                    type="button"
                    role="tab"
                    aria-selected={secao === s.chave}
                    onClick={() => definirSecao(s.chave)}
                    className={`inline-flex min-h-8 items-center gap-2 rounded-botao px-2 text-left text-[11.5px] font-semibold transition-colors ${
                      secao === s.chave
                        ? "bg-realce text-tinta"
                        : "text-tinta-2 hover:bg-painel-2"
                    }`}
                  >
                    <span
                      className={
                        secao === s.chave ? "text-azul-claro" : "text-tinta-3"
                      }
                    >
                      {s.icone}
                    </span>
                    {s.rotulo}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <section
          aria-labelledby="titulo-da-secao"
          className="grid min-w-0 content-start gap-3"
        >
          <header className="grid gap-0.5">
            <h2
              id="titulo-da-secao"
              className="flex items-center gap-2 text-[15px] font-bold text-tinta"
            >
              <span className="text-azul-claro">{atual.icone}</span>
              {atual.rotulo}
            </h2>
            <p className="max-w-prose text-[11.5px] text-tinta-3">
              {atual.descricao}
            </p>
          </header>
          {secao === "usuarios" && <Usuarios />}
          {secao === "chaves" && <Chaves />}
          {secao === "licencas" && <Licencas />}
          {secao === "tarifas" && <TarifasDoContrato />}
          {secao === "guarda" && <GuardaDasImagens />}
          {secao === "sistema" && <Sistema />}
          {secao === "ias" && <IasEFontes />}
          {secao === "senatran" && <IntegracaoSenatran />}
          {secao === "coleta" && <ColetaDosPorticos />}
          {secao === "notificacoes" && <DestinosDasNotificacoes />}
          {secao === "whatsbi" && <WhatsAppECelulares />}
          {secao === "modelo" && <ModeloEConsumo />}
          {secao === "conversas" && <ConversasDoWhatsBi />}
          {secao === "portal" && <PortalDePagamento />}
          {secao === "trilha" && <TrilhaAdministrativa />}
        </section>
      </div>
    </>
  );
}

/** Carrega, trata 402/403 e erro com as frases certas — as quatro seções fazem o mesmo. */
function useRecurso<T>(caminho: string) {
  const [dado, definirDado] = useState<T | null>(null);
  const [erro, definirErro] = useState<ErroDaApi | null>(null);

  const carregar = useCallback(async () => {
    try {
      definirDado(await api<T>(caminho));
      definirErro(null);
    } catch (falha) {
      definirErro(
        falha instanceof ErroDaApi
          ? falha
          : new ErroDaApi("Falha inesperada.", 0),
      );
      definirDado(null);
    }
  }, [caminho]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  return { dado, erro, recarregar: carregar };
}

function Quadro({
  titulo,
  erro,
  vazio,
  children,
  acoes,
  rodape,
}: {
  titulo: string;
  erro: ErroDaApi | null;
  vazio: boolean;
  children: ReactNode;
  acoes?: ReactNode;
  rodape?: ReactNode;
}) {
  return (
    <Painel
      titulo={titulo}
      {...(acoes ? { acoes } : {})}
      {...(rodape ? { rodape } : {})}
    >
      {erro ? (
        <Estado
          tipo={erro.status === 403 ? "bloqueado" : "erro"}
          titulo={
            erro.status === 403
              ? "Esta área é do administrador da organização."
              : "Não foi possível carregar."
          }
          detalhe={erro.message}
        />
      ) : vazio ? (
        <Estado tipo="carregando" titulo="Carregando…" />
      ) : (
        children
      )}
    </Painel>
  );
}

// --------------------------------------------------------------------------- usuários

const PERFIS = ["OPERADOR", "SUPERVISOR", "BACKOFFICE", "ADMIN"] as const;

/** O perfil como a pessoa o reconhece — o código ("ADMIN") fica no `value`. */
const ROTULO_DO_PERFIL: Record<string, string> = {
  OPERADOR: "Operador",
  SUPERVISOR: "Supervisor",
  BACKOFFICE: "Backoffice",
  ADMIN: "Administrador",
  APRESENTADOR: "Apresentador",
  REGULADOR: "Regulador",
};

/**
 * Os perfis que a tela oferece. O Apresentador (`O-06`, ADR-062) só em organização de
 * demonstração — o banco recusa nas outras — e sempre na linha de quem já o tem. O
 * Regulador (`OB-13`, ADR-075) nasce no formulário, que pede o recorte: na linha de quem
 * enxerga todas as concessões, trocar o perfil para ele seria recusado.
 */
function perfisOferecidos(
  demonstracao: boolean,
  atual?: string,
  { novo = false }: { novo?: boolean } = {},
): string[] {
  const perfis: string[] = [...PERFIS];
  if (demonstracao || atual === "APRESENTADOR") perfis.push("APRESENTADOR");
  if (novo || atual === "REGULADOR") perfis.push("REGULADOR");
  return perfis;
}

/** O que o perfil Regulador é, dito onde ele se escolhe. */
const SOBRE_O_REGULADOR =
  "O poder concedente: só lê a SENATRAN, os Prazos, os Indicadores e o Poder concedente, sem a foto de pista, e cada tela que ele abre fica registrada. Precisa de pelo menos uma concessão.";

/** A política da API (T-12): o tamanho é o único critério que a tela confere sozinha. */
const TAMANHO_MINIMO_DA_SENHA = 12;
const DICA_DA_SENHA =
  "Ao menos 12 caracteres. Uma frase curta funciona bem; senhas conhecidas, sequências e o nome da pessoa são recusados.";

/**
 * O perfil de uma linha (`N251`). Mudar o perfil vai à trilha, e um `<select>` que grava a cada
 * mudança grava (e registra) cada degrau de quem percorre as opções com as setas: a escolha
 * fica no rascunho até o "Salvar perfil".
 */
function PerfilDaLinha({
  nome,
  atual,
  oferecidos,
  aoSalvar,
}: {
  nome: string;
  atual: string;
  oferecidos: string[];
  aoSalvar: (perfil: string) => void;
}) {
  const [escolhido, definirEscolhido] = useState(atual);
  return (
    <>
      <select
        value={escolhido}
        aria-label={`Perfil de ${nome}`}
        onChange={(e) => definirEscolhido(e.target.value)}
        className="min-h-8 rounded-botao border border-linha bg-painel px-2 text-[11.5px] text-tinta-2"
      >
        {oferecidos.map((p) => (
          <option key={p} value={p}>
            {ROTULO_DO_PERFIL[p] ?? p}
          </option>
        ))}
      </select>
      {escolhido !== atual && (
        <Botao
          papel="primaria"
          aria-label={`Salvar o perfil de ${nome}`}
          onClick={() => aoSalvar(escolhido)}
        >
          Salvar perfil
        </Botao>
      )}
    </>
  );
}

function Usuarios() {
  const { dado, erro, recarregar } =
    useRecurso<UsuarioDaOrganizacao[]>("/admin/usuarios");
  // As concessões, para o recorte do regulador (`OB-13`): ele sempre tem um.
  const concessoes =
    useRecurso<ConcessaoDaOrganizacao[]>("/admin/concessoes").dado ?? [];
  // De quem o recorte está aberto para mudar. Um por vez, como a senha.
  const [recorteDe, definirRecorteDe] = useState<string | null>(null);
  // Contexto lido direto: a tela também roda em teste sem sessão.
  const demonstracao = Boolean(
    useContext(ContextoDaSessao)?.quem?.modo_demonstracao,
  );
  const [recusa, definirRecusa] = useState<string | null>(null);
  // A ação que espera um "sim": desativar e redefinir o segundo fator derrubam as sessões
  // da pessoa na hora. Uma por vez.
  const [confirmando, definirConfirmando] = useState<{
    id: string;
    acao: "desativar" | "2fa";
  } | null>(null);
  const [criando, definirCriando] = useState(false);
  // Quem está com o formulário de senha nova aberto. Um por vez: dois abertos e a senha
  // digitada num acaba no outro com um clique errado.
  const [trocandoSenhaDe, definirTrocandoSenhaDe] = useState<string | null>(
    null,
  );

  async function mudar(
    id: string,
    corpo: Record<string, unknown>,
  ): Promise<boolean> {
    definirRecusa(null);
    try {
      await api(`/admin/usuarios/${id}`, { method: "PATCH", corpo });
      await recarregar();
      return true;
    } catch (falha) {
      // A recusa do último administrador é 409 com um texto que explica o porquê. Ela
      // aparece inteira: é a diferença entre "não deu" e "não dá, e aqui está o motivo".
      definirRecusa(
        falha instanceof ErroDaApi ? falha.message : "Não foi possível mudar.",
      );
      return false;
    }
  }

  return (
    <Quadro
      titulo="Quem tem acesso"
      erro={erro}
      vazio={dado === null}
      acoes={
        <Botao papel="primaria" onClick={() => definirCriando((c) => !c)}>
          {criando ? "Cancelar" : "Novo usuário"}
        </Botao>
      }
      rodape={<span>{dado ? `${dado.length} usuário(s)` : "—"}</span>}
    >
      {criando && (
        <NovoUsuarioForm
          demonstracao={demonstracao}
          concessoes={concessoes}
          aoCriar={async () => {
            definirCriando(false);
            await recarregar();
          }}
          aoFalhar={definirRecusa}
        />
      )}

      {recusa && (
        <p
          role="alert"
          className="rounded-botao border border-vermelho-borda bg-vermelho-fundo px-2.5 py-2 text-[11.5px] wrap-anywhere text-vermelho"
        >
          {recusa}
        </p>
      )}

      <ul aria-label="Usuários" className="grid gap-1.5">
        {(dado ?? []).map((u) => (
          <li
            key={u.id}
            className="grid items-center gap-2 rounded-[7px] border border-linha bg-painel-2 px-2.5 py-2 lg:grid-cols-[1fr_auto]"
          >
            <span className="min-w-0">
              <b className="block truncate text-tinta">{u.nome}</b>
              <small className="block truncate text-[11px] text-tinta-3">
                {u.email} ·{" "}
                {u.sessoes_ativas > 0
                  ? `${u.sessoes_ativas} sessão(ões) ativa(s)`
                  : "sem sessão ativa"}
                {/* Vazio significa "todas as concessões", e não "nenhuma" (T-05). Dizer
                    isso evita a leitura errada de que o usuário está sem acesso. */}
                {u.concessoes.length === 0
                  ? " · todas as concessões"
                  : ` · ${u.concessoes.length} concessão(ões)`}
              </small>
            </span>
            {/* No celular, as ações descem para baixo do nome e quebram linha: lado a lado,
                elas empurravam o nome para fora da tela. */}
            <span className="flex flex-wrap items-center gap-1.5">
              <PerfilDaLinha
                key={`${u.id}-${u.perfil}`}
                nome={u.nome}
                atual={u.perfil}
                oferecidos={perfisOferecidos(demonstracao, u.perfil)}
                aoSalvar={(perfil) => void mudar(u.id, { perfil })}
              />
              <Badge cor={u.ativo ? "verde" : "cinza"}>
                {u.ativo ? "ativo" : "inativo"}
              </Badge>
              {/* O segundo fator (T-12). Redefinir é para quem perdeu ou trocou de celular:
                  apaga o cadastro, derruba as sessões, e no próximo login a pessoa lê um QR
                  novo. A API recusa redefinir o próprio. */}
              {u.segundo_fator && (
                <>
                  <Badge cor="azul">2FA</Badge>
                  <Botao
                    onClick={() =>
                      definirConfirmando({ id: u.id, acao: "2fa" })
                    }
                    aria-expanded={
                      confirmando?.id === u.id && confirmando.acao === "2fa"
                    }
                    aria-label={`Redefinir o segundo fator de ${u.nome}`}
                  >
                    Redefinir 2FA
                  </Botao>
                </>
              )}
              {/* A rotação de credencial do `T-11` mora aqui: o administrador troca a
                  própria senha e a de quem vai entrar na reunião, sem `UPDATE` à mão. */}
              <Botao
                onClick={() =>
                  definirTrocandoSenhaDe((atual) =>
                    atual === u.id ? null : u.id,
                  )
                }
                aria-expanded={trocandoSenhaDe === u.id}
                aria-label={`Trocar senha de ${u.nome}`}
              >
                {trocandoSenhaDe === u.id ? "Cancelar" : "Trocar senha"}
              </Botao>
              {u.perfil === "REGULADOR" && (
                <Botao
                  onClick={() =>
                    definirRecorteDe((atual) => (atual === u.id ? null : u.id))
                  }
                  aria-expanded={recorteDe === u.id}
                  aria-label={`Concessões de ${u.nome}`}
                >
                  {recorteDe === u.id ? "Cancelar" : "Concessões"}
                </Botao>
              )}
              {/* Reativar devolve o acesso e não pede confirmação; desativar derruba as sessões
                  na hora, e fica a um clique de Trocar senha — por isso pergunta (§4, regra 5). */}
              <Botao
                aria-label={`${u.ativo ? "Desativar" : "Reativar"} ${u.nome}`}
                aria-expanded={
                  u.ativo
                    ? confirmando?.id === u.id &&
                      confirmando.acao === "desativar"
                    : undefined
                }
                onClick={() =>
                  u.ativo
                    ? definirConfirmando({ id: u.id, acao: "desativar" })
                    : void mudar(u.id, { ativo: true })
                }
              >
                {u.ativo ? "Desativar" : "Reativar"}
              </Botao>
            </span>
            {confirmando?.id === u.id && (
              <div
                role="group"
                aria-label={`Confirmar para ${u.nome}`}
                className="flex flex-wrap items-center gap-2 rounded-botao border border-amarelo-borda bg-amarelo-fundo px-2.5 py-2 text-[11.5px] text-amarelo lg:col-span-2"
              >
                <span className="min-w-0 flex-1 wrap-anywhere">
                  {confirmando.acao === "desativar"
                    ? `Desativar ${u.nome}? As sessões abertas dela caem agora.`
                    : `Redefinir o segundo fator de ${u.nome}? As sessões caem, e no próximo login ela lê um QR novo.`}
                </span>
                <Botao
                  papel="primaria"
                  onClick={async () => {
                    const ok = await mudar(
                      u.id,
                      confirmando.acao === "desativar"
                        ? { ativo: false }
                        : { redefinir_segundo_fator: true },
                    );
                    if (ok) definirConfirmando(null);
                  }}
                >
                  {confirmando.acao === "desativar"
                    ? "Sim, desativar"
                    : "Sim, redefinir"}
                </Botao>
                <Botao onClick={() => definirConfirmando(null)}>Voltar</Botao>
              </div>
            )}
            {recorteDe === u.id && (
              <RecorteDoRegulador
                nome={u.nome}
                concessoes={concessoes}
                inicial={u.concessoes}
                aoSalvar={async (escolhidas) => {
                  if (await mudar(u.id, { concessoes: escolhidas }))
                    definirRecorteDe(null);
                }}
              />
            )}
            {trocandoSenhaDe === u.id && (
              <NovaSenhaForm
                nome={u.nome}
                aoSalvar={async (senha) => {
                  if (await mudar(u.id, { senha }))
                    definirTrocandoSenhaDe(null);
                }}
              />
            )}
          </li>
        ))}
      </ul>
    </Quadro>
  );
}

function NovoUsuarioForm({
  aoCriar,
  aoFalhar,
  demonstracao,
  concessoes = [],
}: {
  aoCriar: () => Promise<void>;
  aoFalhar: (mensagem: string) => void;
  demonstracao: boolean;
  concessoes?: ConcessaoDaOrganizacao[];
}) {
  const [nome, definirNome] = useState("");
  const [email, definirEmail] = useState("");
  const [senha, definirSenha] = useState("");
  const [perfil, definirPerfil] = useState<string>("OPERADOR");
  const [recorte, definirRecorte] = useState<string[]>([]);
  const regulador = perfil === "REGULADOR";

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    try {
      await api("/admin/usuarios", {
        method: "POST",
        // Só o regulador leva o recorte daqui: os outros perfis nascem vendo todas, como
        // sempre (`T-05`).
        corpo: {
          nome,
          email,
          senha,
          perfil,
          concessoes: regulador ? recorte : [],
        },
      });
      await aoCriar();
    } catch (falha) {
      aoFalhar(
        falha instanceof ErroDaApi ? falha.message : "Não foi possível criar.",
      );
    }
  }

  return (
    <form
      onSubmit={(e) => void enviar(e)}
      className="grid gap-2 rounded-cartao border border-linha bg-painel-2 p-2.5 [grid-template-columns:repeat(auto-fit,minmax(170px,1fr))]"
    >
      <Campo rotulo="Nome" valor={nome} aoMudar={definirNome} />
      <Campo
        rotulo="E-mail"
        valor={email}
        aoMudar={definirEmail}
        tipo="email"
      />
      <Campo
        rotulo="Senha"
        valor={senha}
        aoMudar={definirSenha}
        tipo="password"
      />
      <label className="grid gap-1 text-[11px] text-tinta-3">
        Perfil
        <select
          value={perfil}
          aria-label="Perfil"
          onChange={(e) => definirPerfil(e.target.value)}
          className="min-h-8 rounded-botao border border-linha bg-painel px-2 text-[11.5px] text-tinta-2"
        >
          {perfisOferecidos(demonstracao, undefined, { novo: true }).map(
            (p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ),
          )}
        </select>
      </label>
      <button
        type="submit"
        disabled={regulador && recorte.length === 0}
        className="min-h-8 self-end rounded-botao border border-azul-cheio bg-azul-cheio px-2.5 text-[11.5px] font-semibold text-white disabled:opacity-50"
      >
        Criar
      </button>
      {regulador && (
        <div className="col-span-full grid gap-1.5">
          <small className="text-[11px] text-tinta-3">
            {SOBRE_O_REGULADOR}
          </small>
          <EscolhaDeConcessoes
            concessoes={concessoes}
            escolhidas={recorte}
            aoMudar={definirRecorte}
          />
        </div>
      )}
    </form>
  );
}

/** As concessões de um recorte, uma caixa por concessão ativa. */
function EscolhaDeConcessoes({
  concessoes,
  escolhidas,
  aoMudar,
}: {
  concessoes: ConcessaoDaOrganizacao[];
  escolhidas: string[];
  aoMudar: (escolhidas: string[]) => void;
}) {
  const ativas = concessoes.filter((c) => c.ativo);
  if (ativas.length === 0)
    return (
      <small className="text-[11px] text-amarelo">
        Nenhuma concessão ativa para o recorte.
      </small>
    );
  return (
    <fieldset className="flex flex-wrap gap-x-3 gap-y-1">
      <legend className="sr-only">Concessões do recorte</legend>
      {ativas.map((c) => (
        <label
          key={c.id}
          className="flex items-center gap-1.5 text-[11.5px] text-tinta-2"
        >
          <input
            type="checkbox"
            checked={escolhidas.includes(c.id)}
            onChange={(e) =>
              aoMudar(
                e.target.checked
                  ? [...escolhidas, c.id]
                  : escolhidas.filter((id) => id !== c.id),
              )
            }
          />
          {c.nome} <span className="text-tinta-3">({c.codigo})</span>
        </label>
      ))}
    </fieldset>
  );
}

/** O recorte de um regulador que já existe: mudar fica na trilha administrativa. */
function RecorteDoRegulador({
  nome,
  concessoes,
  inicial,
  aoSalvar,
}: {
  nome: string;
  concessoes: ConcessaoDaOrganizacao[];
  inicial: string[];
  aoSalvar: (escolhidas: string[]) => Promise<void>;
}) {
  const [escolhidas, definirEscolhidas] = useState<string[]>(inicial);
  return (
    <form
      aria-label={`Concessões de ${nome}`}
      className="col-span-full grid gap-2 border-t border-linha-suave pt-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (escolhidas.length > 0) void aoSalvar(escolhidas);
      }}
    >
      <EscolhaDeConcessoes
        concessoes={concessoes}
        escolhidas={escolhidas}
        aoMudar={definirEscolhidas}
      />
      <span className="flex flex-wrap items-center gap-2">
        <Botao
          papel="primaria"
          type="submit"
          disabled={escolhidas.length === 0}
        >
          Salvar o recorte
        </Botao>
        <small className="text-[11px] text-tinta-3">
          O regulador precisa de pelo menos uma concessão. Para tirar o acesso,
          desative.
        </small>
      </span>
    </form>
  );
}

/**
 * Senha nova para um usuário. Duas vezes, e só habilita quando as duas batem e têm o
 * mínimo que a API exige (12, T-12) — a validação do servidor continua valendo, e é ela
 * que recusa a senha vazada, a sequência e a que repete o nome, com a frase de cada caso;
 * esta só evita a ida e volta pelo erro mais comum, que é o dedo.
 */
function NovaSenhaForm({
  nome,
  aoSalvar,
}: {
  nome: string;
  aoSalvar: (senha: string) => Promise<void>;
}) {
  const [senha, definirSenha] = useState("");
  const [confirmacao, definirConfirmacao] = useState("");
  const batem =
    senha.length >= TAMANHO_MINIMO_DA_SENHA && senha === confirmacao;

  return (
    <form
      aria-label={`Nova senha para ${nome}`}
      className="col-span-full grid gap-2 border-t border-linha-suave pt-2 md:grid-cols-[1fr_1fr_auto] md:items-end"
      onSubmit={(e) => {
        e.preventDefault();
        if (batem) void aoSalvar(senha);
      }}
    >
      <Campo
        rotulo="Nova senha"
        valor={senha}
        aoMudar={definirSenha}
        tipo="password"
      />
      <Campo
        rotulo="Repita a nova senha"
        valor={confirmacao}
        aoMudar={definirConfirmacao}
        tipo="password"
      />
      <Botao papel="primaria" type="submit" disabled={!batem}>
        Salvar senha
      </Botao>
      {senha && confirmacao && senha !== confirmacao ? (
        <small className="text-[11px] text-amarelo md:col-span-full">
          As duas não batem.
        </small>
      ) : (
        <small className="text-[11px] text-tinta-3 md:col-span-full">
          {DICA_DA_SENHA}
        </small>
      )}
    </form>
  );
}

function Campo({
  rotulo,
  valor,
  aoMudar,
  tipo = "text",
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  tipo?: string;
}) {
  return (
    <label className="grid gap-1 text-[11px] text-tinta-3">
      {rotulo}
      <input
        type={tipo}
        value={valor}
        required
        aria-label={rotulo}
        onChange={(e) => aoMudar(e.target.value)}
        className="min-h-8 rounded-botao border border-linha bg-painel px-2 text-[11.5px] text-tinta"
      />
    </label>
  );
}

// ------------------------------------------------------------------ chaves de pórtico

function Chaves() {
  const { dado, erro, recarregar } =
    useRecurso<ChaveDoPortico[]>("/admin/chaves");
  const [emitida, definirEmitida] = useState<ChaveEmitida | null>(null);
  const [nome, definirNome] = useState("");
  // O que a chave faz decide o único escopo dela (ADR-010): o pórtico ingere passagem; o
  // sistema de notificação da concessionária registra a prova da notificação (`OB-08`).
  const [finalidade, definirFinalidade] = useState<"portico" | "notificacoes">(
    "portico",
  );

  // Quem está revogando e o motivo digitado. O motivo é pedido na própria linha, como na
  // Lista de isentos: o `prompt()` do navegador não some na recusa, não se lê no celular e
  // não deixa dizer o que deu errado.
  const [revogando, definirRevogando] = useState<{
    id: string;
    motivo: string;
  } | null>(null);
  const [recusa, definirRecusa] = useState<string | null>(null);

  async function emitir() {
    definirRecusa(null);
    try {
      const nova = await api<ChaveEmitida>("/admin/chaves", {
        method: "POST",
        corpo: {
          nome:
            nome ||
            (finalidade === "portico" ? "Pórtico" : "Sistema de notificação"),
          finalidade,
        },
      });
      definirEmitida(nova);
      definirNome("");
      await recarregar();
    } catch (falha) {
      definirRecusa(
        falha instanceof ErroDaApi
          ? falha.message
          : "Não foi possível emitir a chave.",
      );
    }
  }

  async function revogar(id: string, motivo: string) {
    definirRecusa(null);
    try {
      await api(`/admin/chaves/${id}/revogar`, {
        method: "POST",
        corpo: { motivo },
      });
      definirRevogando(null);
      await recarregar();
    } catch (falha) {
      // A linha fica aberta, com o motivo digitado: a pessoa tenta de novo sem reescrever.
      definirRecusa(
        falha instanceof ErroDaApi
          ? falha.message
          : "Não foi possível revogar a chave.",
      );
    }
  }

  return (
    <Quadro
      titulo="Chaves emitidas"
      erro={erro}
      vazio={dado === null}
      rodape={
        <span>
          Chave revogada continua na lista: ela é histórico de um pórtico que
          parou.
        </span>
      }
    >
      <div className="flex flex-wrap items-end gap-2 rounded-cartao border border-linha bg-painel-2 p-2.5">
        <Campo rotulo="Nome da nova chave" valor={nome} aoMudar={definirNome} />
        <label className="grid gap-1 text-[11px] text-tinta-3">
          Para
          <select
            aria-label="Para que é a chave"
            value={finalidade}
            onChange={(e) =>
              definirFinalidade(e.target.value as "portico" | "notificacoes")
            }
            className="min-h-8 rounded-botao border border-linha bg-painel px-2 text-[11.5px] text-tinta"
          >
            <option value="portico">Pórtico (ingestão)</option>
            <option value="notificacoes">Sistema de notificação</option>
          </select>
        </label>
        <Botao papel="primaria" onClick={() => void emitir()}>
          <Icone.Chave />
          Emitir
        </Botao>
        <p className="text-[10.5px] text-tinta-3">
          {finalidade === "portico" ? (
            <>
              O escopo é <b>ingestão</b>, e não há campo que o alargue: uma
              chave vive numa caixa na beira da rodovia (ADR-010).
            </>
          ) : (
            <>
              O escopo é <b>notificações</b>: o sistema da concessionária
              registra a prova da notificação ao usuário, e só isso — não lê a
              fila nem ingere passagem.
            </>
          )}
        </p>
      </div>

      {recusa && (
        <p
          role="alert"
          className="rounded-botao border border-vermelho-borda bg-vermelho-fundo px-2.5 py-2 text-[11.5px] wrap-anywhere text-vermelho"
        >
          {recusa}
        </p>
      )}

      {/* A única vez que o segredo existe. Depois daqui, o banco tem só o SHA-256 dele. */}
      {emitida && (
        <Cartao
          titulo="Guarde agora — o segredo não volta a aparecer"
          icone={<Icone.Alerta />}
        >
          <p className="mono rounded-botao border border-amarelo-borda bg-amarelo-fundo px-2.5 py-2 text-[11.5px] wrap-anywhere text-amarelo">
            {emitida.segredo}
          </p>
          <p className="text-[10.5px] text-tinta-3">{emitida.aviso}</p>
          <Botao onClick={() => definirEmitida(null)}>Já guardei</Botao>
        </Cartao>
      )}

      <ul aria-label="Chaves" className="grid gap-1.5">
        {(dado ?? []).map((c) => (
          <li
            key={c.id}
            className="grid grid-cols-[1fr_auto] items-center gap-2 rounded-[7px] border border-linha bg-painel-2 px-2.5 py-2"
          >
            <span className="min-w-0">
              <b className="block truncate text-tinta">{c.nome}</b>
              <small className="mono block truncate text-[10.5px] text-tinta-3">
                {c.prefixo}
                {c.escopos.includes("notificacoes") && " · notificações"}
                {c.escopos.includes("coleta") && " · coletor"}
              </small>
              <small className="block text-[11px] text-tinta-3">
                {/* "Nunca usada" e "usada há um mês" são fatos diferentes, e os dois são
                    sinal: um é instalação que não terminou, o outro é pórtico que parou. */}
                {c.ultimo_uso_em
                  ? `último uso em ${dataEHora(c.ultimo_uso_em)}`
                  : "nunca usada"}
                {c.revogada_em &&
                  ` · revogada: ${c.motivo_revogacao ?? "sem motivo"}`}
              </small>
            </span>
            <span className="flex items-center gap-1.5">
              <Badge cor={c.revogada_em ? "cinza" : "verde"}>
                {c.revogada_em ? "revogada" : "ativa"}
              </Badge>
              {!c.revogada_em && (
                <Botao
                  aria-label={`Revogar a chave ${c.nome}`}
                  aria-expanded={revogando?.id === c.id}
                  onClick={() =>
                    definirRevogando((atual) =>
                      atual?.id === c.id ? null : { id: c.id, motivo: "" },
                    )
                  }
                >
                  Revogar
                </Botao>
              )}
            </span>
            {revogando?.id === c.id && (
              <form
                aria-label={`Motivo da revogação de ${c.nome}`}
                className="col-span-2 flex flex-wrap items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (revogando.motivo.trim())
                    void revogar(c.id, revogando.motivo.trim());
                }}
              >
                <label className="grid min-w-0 flex-1 gap-1 text-[11px] text-tinta-3">
                  Por que esta chave está sendo revogada?
                  <input
                    type="text"
                    value={revogando.motivo}
                    onChange={(e) =>
                      definirRevogando({ id: c.id, motivo: e.target.value })
                    }
                    autoFocus
                    className="min-h-8 rounded-botao border border-linha bg-painel px-2 text-[11.5px] text-tinta"
                  />
                </label>
                <Botao
                  papel="primaria"
                  disabled={!revogando.motivo.trim()}
                  onClick={() => void revogar(c.id, revogando.motivo.trim())}
                >
                  Revogar a chave
                </Botao>
              </form>
            )}
          </li>
        ))}
      </ul>
    </Quadro>
  );
}

// ----------------------------------------------------------------- licenças e consumo

/** O nome de cada capacidade cobrável como o administrador a reconhece — o código fica no
 *  `title`, para quem confere com a fatura. */
const ROTULO_DA_CAPACIDADE: Record<string, string> = {
  ocr_placa: "Leitura de placa",
  contagem_eixos: "Contagem de eixos",
  classificacao_veiculo: "Classificação do veículo",
  anonimizacao: "Anonimização de imagem",
  conversa_agente: "Perguntas ao agente (WhatsBI)",
  rotear_pergunta: "Roteamento de pergunta",
};

/** Por que a chamada não foi cobrada — o enum `core.motivo_nao_faturavel`, por extenso. */
const ROTULO_DO_MOTIVO: Record<string, string> = {
  FALHA_NOSSA: "falha do cockpit",
  ENTRADA_INADEQUADA: "imagem que não servia",
  COTA_ESGOTADA: "cota do contrato esgotada",
  REPROCESSAMENTO_POR_FALHA: "reprocessamento por falha",
  CORTESIA: "cortesia (regras, sem modelo)",
  CHAVE_DO_CLIENTE: "pago na chave do cliente",
};

function Licencas() {
  const { dado, erro } = useRecurso<PainelDeLicencas>("/admin/licencas");
  const tetos = useRecurso<TetosDoContrato>("/admin/cotas");

  return (
    <Quadro
      titulo="Licenças e consumo de IA (30 dias)"
      erro={erro}
      vazio={dado === null}
      rodape={
        <span>Cada chamada de capacidade é uma linha de fatura (ADR-012).</span>
      }
    >
      <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(280px,1fr))]">
        <Cartao titulo="Módulos" icone={<Icone.Lista />}>
          {(dado?.modulos ?? []).map((m) => (
            <Linha key={m.codigo} rotulo={m.nome}>
              <Badge cor={m.licenciado ? "verde" : "cinza"}>
                {m.licenciado ? "licenciado" : "não contratado"}
              </Badge>
            </Linha>
          ))}
        </Cartao>

        <Cartao titulo="Consumo por capacidade" icone={<Icone.Dinheiro />}>
          {(dado?.consumo ?? []).length === 0 ? (
            <p className="text-[11.5px] text-tinta-3">
              Nenhuma chamada de IA nos últimos 30 dias.
            </p>
          ) : (
            (dado?.consumo ?? []).map((c) => (
              <Linha
                key={`${c.modulo}.${c.capacidade}`}
                rotulo={ROTULO_DA_CAPACIDADE[c.capacidade] ?? c.capacidade}
                mono
              >
                <span title={`${c.modulo}.${c.capacidade}`}>
                  {numeroLongo(c.faturaveis)} cobradas de{" "}
                  {numeroLongo(c.chamadas)}
                </span>
              </Linha>
            ))
          )}
        </Cartao>
      </div>

      <CotasDoContrato tetos={tetos.dado} />

      {/* O motivo de não cobrar foi gravado no ato de cada chamada, nunca inferido depois.
          Mostrá-lo é o que torna a conciliação do mês uma leitura em vez de uma auditoria. */}
      {(dado?.consumo ?? []).some((c) => Object.keys(c.motivos).length > 0) && (
        <Cartao titulo="Por que não foi cobrado" icone={<Icone.Escudo />}>
          {(dado?.consumo ?? []).flatMap((c) =>
            Object.entries(c.motivos).map(([motivo, quantas]) => (
              <Linha
                key={`${c.capacidade}-${motivo}`}
                rotulo={`${ROTULO_DA_CAPACIDADE[c.capacidade] ?? c.capacidade} · ${ROTULO_DO_MOTIVO[motivo] ?? motivo}`}
                mono
              >
                {numeroLongo(quantas)}
              </Linha>
            )),
          )}
        </Cartao>
      )}
    </Quadro>
  );
}

/**
 * As cotas e os tetos do contrato (`B-03`, `A-09`, ADR-070): quanto o contrato deixa usar no
 * mês e quanto já foi — o cobrado e o que já está na fila. Quem amplia é o dono do banco, a
 * pedido da IA Solutions; daqui não se muda nada.
 */
function CotasDoContrato({ tetos }: { tetos: TetosDoContrato | null }) {
  if (!tetos) return null;
  const semTeto =
    tetos.cotas.length === 0 &&
    tetos.imagens_por_lote == null &&
    tetos.teto_de_usuarios == null;
  return (
    <Cartao titulo="Cotas do contrato (este mês)" icone={<Icone.Alvo />}>
      {semTeto ? (
        <p className="text-[11.5px] text-tinta-3">
          Sem cota: o contrato não limita o uso.
        </p>
      ) : (
        <>
          {tetos.cotas.map((c) => (
            <Linha
              key={`${c.modulo}.${c.capacidade}`}
              rotulo={ROTULO_DA_CAPACIDADE[c.capacidade] ?? c.rotulo}
              mono
            >
              <span
                className="flex items-center gap-1.5"
                title={
                  c.reservado
                    ? `${numeroLongo(c.usado)} cobradas e ${numeroLongo(c.reservado)} na fila`
                    : `${numeroLongo(c.usado)} cobradas`
                }
              >
                {usoDaCota(c)}
                <Badge cor={COR_DA_COTA[c.situacao]}>
                  {c.situacao === "esgotada"
                    ? "acabou"
                    : `${String(c.percentual)}%`}
                </Badge>
              </span>
            </Linha>
          ))}
          {tetos.imagens_por_lote != null && (
            <Linha rotulo="Imagens por lote" mono>
              no máximo {numeroLongo(tetos.imagens_por_lote)}
            </Linha>
          )}
          <Linha rotulo="Usuários ativos" mono>
            {tetos.teto_de_usuarios != null
              ? `${numeroLongo(tetos.usuarios_ativos)} de ${numeroLongo(tetos.teto_de_usuarios)}`
              : `${numeroLongo(tetos.usuarios_ativos)}, sem teto`}
          </Linha>
          <p className="text-[11px] text-tinta-3">
            Só o que é cobrado gasta a cota; a fila já entra na conta. Renova em{" "}
            {dataDaCota(tetos.renova_em)}. Para ampliar, fale com a IA
            Solutions.
          </p>
        </>
      )}
    </Cartao>
  );
}

// ------------------------------------------------------------- IAs e fontes de dados

const COR_DA_FONTE = {
  ativa: "verde",
  parada: "amarelo",
  nao_configurada: "cinza",
} as const;
const ROTULO_DA_FONTE: Record<string, string> = {
  ativa: "recebendo",
  parada: "sem nada em 24 h",
  nao_configurada: "não configurado",
};

function IasEFontes() {
  const ias = useRecurso<SituacaoDasIas>("/visao/situacao");
  const fontes = useRecurso<FonteDeDados[]>("/admin/fontes");
  const ativas = (ias.dado?.ias ?? []).filter((i) => i.ativa).length;

  // A área de conteúdo da moldura não rola (§3 do design system): com dois quadros, quem
  // rola é este contêiner — sem ele, as fontes de dados ficavam cortadas embaixo.
  return (
    <div className="flex min-h-0 flex-col gap-3 *:shrink-0 md:overflow-y-auto">
      <Quadro
        titulo="IAs em serviço"
        erro={ias.erro}
        vazio={ias.dado === null}
        acoes={
          ias.dado && (
            <Badge
              cor={
                !ias.dado.servico_no_ar
                  ? "vermelho"
                  : ativas === ias.dado.ias.length
                    ? "verde"
                    : "amarelo"
              }
            >
              {ias.dado.servico_no_ar
                ? `${ativas} de ${ias.dado.ias.length} ativas`
                : "serviço de visão fora do ar"}
            </Badge>
          )
        }
        rodape={
          <span>
            "Ativa" é o que o serviço de visão declara servir agora; os números
            são do extrato de consumo desta organização nas últimas 24 horas.
          </span>
        }
      >
        {ias.dado && !ias.dado.servico_no_ar && (
          <p className="text-[11.5px] text-vermelho">
            {ias.dado.motivo ?? "O serviço de visão não respondeu."}
          </p>
        )}
        <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
          {(ias.dado?.ias ?? []).map((ia) => {
            // O que pinta o selo é a **última** chamada, não a soma do dia: falhas de manhã
            // seguidas de sucesso são uma IA que voltou (21/09: a classificação passou a ser
            // servida no meio do dia, e o selo ficou vermelho por 14 falhas antigas).
            const ultimaDeuCerto =
              ia.ultimo_sucesso_em != null &&
              ia.ultimo_sucesso_em === ia.ultima_chamada_em;
            const [cor, rotulo] = !ia.ativa
              ? (["cinza", "não servida"] as const)
              : ia.falhas_24h === 0 || ia.chamadas_24h === 0
                ? (["verde", "ativa"] as const)
                : ultimaDeuCerto
                  ? (["verde", "ativa · voltou"] as const)
                  : (["vermelho", "última chamada falhou"] as const);
            return (
              <Cartao
                key={ia.capacidade}
                titulo={ia.rotulo}
                icone={<Icone.Alvo />}
              >
                <Linha rotulo="Situação">
                  <Badge cor={cor}>{rotulo}</Badge>
                </Linha>
                <Linha rotulo="Modelo" mono>
                  {ia.modelo ?? "—"}
                </Linha>
                <Linha rotulo="Chamadas (24 h)" mono>
                  {numeroLongo(ia.chamadas_24h)}
                  {ia.falhas_24h > 0 && (
                    <span className="text-vermelho">
                      {` · ${ia.falhas_24h} falha(s)`}
                    </span>
                  )}
                </Linha>
                {(ia.entradas_inadequadas_24h ?? 0) > 0 && (
                  <Linha rotulo="Sem resultado (imagem não servia)" mono>
                    {numeroLongo(ia.entradas_inadequadas_24h)}
                  </Linha>
                )}
                <Linha rotulo="Tempo médio" mono>
                  {ia.duracao_media_ms == null
                    ? "—"
                    : `${ia.duracao_media_ms} ms`}
                </Linha>
                <Linha rotulo="Última chamada" mono>
                  {ia.ultima_chamada_em ? dataEHora(ia.ultima_chamada_em) : "—"}
                </Linha>
                {ia.falhas_24h > 0 && (
                  <Linha rotulo="Última que deu certo" mono>
                    {ia.ultimo_sucesso_em
                      ? dataEHora(ia.ultimo_sucesso_em)
                      : "nenhuma em 24 h"}
                  </Linha>
                )}
              </Cartao>
            );
          })}
        </div>
      </Quadro>

      <Quadro
        titulo="Fontes de dados e saúde da conexão"
        erro={fontes.erro}
        vazio={fontes.dado === null}
        acoes={
          <Botao onClick={() => void fontes.recarregar()}>
            <Icone.Recarga />
            Atualizar
          </Botao>
        }
        rodape={
          <span>
            Hoje os trânsitos entram por upload e pela API do pórtico. Banco e
            data lake são a próxima etapa: os campos abaixo são o que a conexão
            vai pedir à TI.
          </span>
        }
      >
        <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(280px,1fr))]">
          {(fontes.dado ?? []).map((f) => (
            <Cartao key={f.codigo} titulo={f.nome} icone={<Icone.Enviar />}>
              <Linha rotulo="Situação">
                <Badge
                  cor={
                    COR_DA_FONTE[f.situacao as keyof typeof COR_DA_FONTE] ??
                    "cinza"
                  }
                >
                  {ROTULO_DA_FONTE[f.situacao] ?? f.situacao}
                </Badge>
              </Linha>
              <Linha rotulo="Tipo">{f.tipo}</Linha>
              {f.situacao !== "nao_configurada" && (
                <>
                  <Linha rotulo="Último recebimento" mono>
                    {f.ultimo_recebimento
                      ? dataEHora(f.ultimo_recebimento)
                      : "nunca"}
                  </Linha>
                  <Linha rotulo="Última hora · 24 h" mono>
                    {numeroLongo(f.volume_1h)} · {numeroLongo(f.volume_24h)}
                  </Linha>
                  <Linha rotulo="Erros (24 h)" mono>
                    <span className={f.erros_24h > 0 ? "text-vermelho" : ""}>
                      {numeroLongo(f.erros_24h)}
                    </span>
                  </Linha>
                </>
              )}
              <p className="text-[11px] text-tinta-3">{f.detalhe}</p>
              {(f.campos ?? []).length > 0 && (
                <fieldset disabled className="grid gap-1.5 opacity-70">
                  {(f.campos ?? []).map((c) => (
                    <label
                      key={c.nome}
                      className="grid gap-0.5 text-[10.5px] text-tinta-3"
                    >
                      {c.rotulo}
                      <input
                        placeholder={c.exemplo}
                        aria-label={`${f.nome}: ${c.rotulo}`}
                        className="mono rounded-botao border border-linha bg-painel-2 px-2 py-1 text-[11px] text-tinta"
                      />
                    </label>
                  ))}
                  <Botao disabled>Testar conexão (em breve)</Botao>
                </fieldset>
              )}
            </Cartao>
          ))}
        </div>
      </Quadro>
    </div>
  );
}

// --------------------------------------------------------------- configuração e saúde

function Sistema() {
  const panorama = useRecurso<Panorama>("/admin/panorama");
  const config = useRecurso<ValorDaConfiguracao[]>("/admin/configuracoes");
  const [recusa, definirRecusa] = useState<string | null>(null);

  async function gravar(chave: string, valor: unknown) {
    definirRecusa(null);
    try {
      await api(`/admin/configuracoes/${chave}`, {
        method: "PUT",
        corpo: { valor },
      });
      await config.recarregar();
    } catch (falha) {
      // A validação é do servidor (T-04). A mensagem dele já é escrita para quem está
      // olhando um formulário — repetir a regra aqui seria manter duas versões dela.
      definirRecusa(
        falha instanceof ErroDaApi ? falha.message : "Não foi possível gravar.",
      );
    }
  }

  const p = panorama.dado;

  return (
    <div className="grid min-h-0 grid-cols-1 gap-3 xl:grid-cols-[1fr_1fr]">
      <Quadro
        titulo="Volume, tráfego e saúde"
        erro={panorama.erro}
        vazio={p === null}
      >
        <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
          <Cartao titulo="Trânsitos (14 dias)" icone={<Icone.Grafico />}>
            <Linha rotulo="Total" mono>
              {(p?.transitos_por_dia ?? []).reduce(
                (s, d) => s + d.transitos,
                0,
              )}
            </Linha>
            {(p?.transitos_por_praca ?? []).map((praca) => (
              <Linha key={praca.nome} rotulo={praca.nome} mono>
                {praca.quantidade}
              </Linha>
            ))}
          </Cartao>

          <Cartao titulo="Imagens armazenadas" icone={<Icone.Foto />}>
            <Linha rotulo="De pista" mono>
              {p?.imagens_do_transito ?? 0}
            </Linha>
            <Linha rotulo="Por upload" mono>
              {p?.imagens_de_analise ?? 0}
            </Linha>
          </Cartao>

          <Cartao titulo="Prazos abertos" icone={<Icone.Relogio />}>
            {(p?.prazos_abertos ?? []).map((prazo) => (
              <Linha
                key={prazo.nome}
                rotulo={prazo.nome.replace(/_/g, " ").toLowerCase()}
                mono
              >
                {prazo.quantidade}
              </Linha>
            ))}
            <Linha rotulo="Vencidos e não processados">
              {/* Zero aqui é o motor de prazos trabalhando. Diferente de zero, é alarme:
                  o worker não rodou, e cada um destes é receita saindo do prazo legal. */}
              <span
                className={p?.prazos_vencidos ? "text-vermelho" : "text-verde"}
              >
                {p?.prazos_vencidos ?? 0}
              </span>
            </Linha>
          </Cartao>

          <Cartao titulo="Acesso" icone={<Icone.Escudo />}>
            <Linha rotulo="Usuários ativos" mono>
              {p?.usuarios_ativos ?? 0}
            </Linha>
            <Linha rotulo="Chaves vivas" mono>
              {p?.chaves_vivas ?? 0}
            </Linha>
            <Linha rotulo="Sem uso há 7 dias">
              <span
                className={
                  p?.chaves_sem_uso_recente ? "text-amarelo" : "text-tinta-2"
                }
              >
                {p?.chaves_sem_uso_recente ?? 0}
              </span>
            </Linha>
          </Cartao>
        </div>
      </Quadro>

      <Quadro
        titulo="Configuração (T-04)"
        erro={config.erro}
        vazio={config.dado === null}
        rodape={
          <span>
            Validado na escrita; o padrão da plataforma vale quando não há
            valor.
          </span>
        }
      >
        {recusa && (
          <p
            role="alert"
            className="rounded-botao border border-vermelho-borda bg-vermelho-fundo px-2.5 py-2 text-[11.5px] wrap-anywhere text-vermelho"
          >
            {recusa}
          </p>
        )}
        <ul aria-label="Configurações" className="grid gap-2">
          {/* As datas do contrato de cada concessão (`PC-02`) só se definem por concessão, e
              quem as informa é a tela do Poder concedente — aqui não teriam a concessão. */}
          {(config.dado ?? [])
            .filter((c) => !c.so_por_concessao)
            .map((c) => (
              <li
                key={c.chave}
                className="grid gap-1 rounded-cartao border border-linha bg-painel-2 p-2.5"
              >
                <span className="flex flex-wrap items-center gap-1.5">
                  <b className="text-tinta">{c.rotulo}</b>
                  {/* Mostrar `70` sem dizer que é o padrão faria o administrador acreditar
                    que alguém escolheu aquele número. */}
                  <Badge
                    cor={c.origem === "padrão da plataforma" ? "cinza" : "azul"}
                  >
                    {c.origem}
                  </Badge>
                </span>
                <p className="max-w-prose text-[11px] text-tinta-3">
                  {c.descricao}
                </p>
                <ValorEditavel
                  valor={c}
                  aoGravar={(v) => void gravar(c.chave, v)}
                />
              </li>
            ))}
        </ul>
      </Quadro>
    </div>
  );
}

function ValorEditavel({
  valor,
  aoGravar,
}: {
  valor: ValorDaConfiguracao;
  aoGravar: (v: unknown) => void;
}) {
  const [rascunho, definirRascunho] = useState(String(valor.valor));

  if (valor.tipo === "booleano") {
    return (
      <Botao onClick={() => aoGravar(!valor.valor)}>
        {valor.valor ? "Ligado — desligar" : "Desligado — ligar"}
      </Botao>
    );
  }
  if (valor.tipo === "texto") {
    // Texto livre, como as palavras dos veículos operacionais (`A-06`). A regra de forma é
    // do servidor, e a recusa dele aparece acima da lista.
    return (
      <span className="flex flex-wrap items-center gap-1.5">
        <input
          value={rascunho}
          aria-label={valor.rotulo}
          onChange={(e) => definirRascunho(e.target.value)}
          className="min-h-8 min-w-60 flex-1 rounded-botao border border-linha bg-painel px-2 text-[11.5px] text-tinta"
        />
        <Botao onClick={() => aoGravar(rascunho)}>Gravar</Botao>
      </span>
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <input
        value={rascunho}
        aria-label={valor.rotulo}
        onChange={(e) => definirRascunho(e.target.value)}
        className="mono min-h-8 w-24 rounded-botao border border-linha bg-painel px-2 text-[11.5px] text-tinta"
      />
      {valor.unidade && (
        <span className="text-[11px] text-tinta-3">{valor.unidade}</span>
      )}
      <Botao onClick={() => aoGravar(Number(rascunho))}>Gravar</Botao>
      <span className="text-[10.5px] text-tinta-3">
        padrão {String(valor.padrao)}
        {valor.minimo !== null && valor.maximo !== null
          ? ` · entre ${valor.minimo} e ${valor.maximo}`
          : ""}
      </span>
    </span>
  );
}
